import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import toast from '../../utils/toast';
import {
  User, Briefcase, Globe, MapPin, CreditCard, ClipboardCheck,
  ChevronLeft, ChevronRight, Check, Plus, AlertCircle, Info,
} from 'lucide-react';

import Modal from '../UI/Modal';
import FormSelect from '../UI/FormSelect';
import SearchableCombobox from '../UI/SearchableCombobox';
import DatePickerField from '../UI/DatePickerField';
import ResidentialAddressEditor from '../profile/ResidentialAddressEditor';
import CountryOfBirthField from '../profile/CountryOfBirthField';
import {
  OVERSEAS_RESIDENTIAL_COUNTRY,
  ensureCountryInAddress,
  inferResidentialAddressLocation,
} from '../../utils/residentialAddressUtils';

import { staffAPI, dormitoryAPI } from '../../services/api';
import { useDepartments, useTradeCategories, useMOMDesignations } from '../../hooks/useConfigData';
import { useStaffList } from '../../hooks/useStaffData';
import { useDirectHire } from '../../hooks/useStaffData';
import { useExternalWorkSites, useRegisterExternalPersonnel } from '../../hooks/useExternalPersonnel';
import { parseLocalDate, toLocalISODate } from '../../utils/dateUtils';
import { formatWorkScheduleTemplateLabel } from '../../utils/scheduleUtils';
import {
  COMPANY_EMAIL_DOMAIN,
  workEmailDomainError,
} from '../../utils/emailUtils';
import { RELIGION_OPTIONS } from '../../constants/religionOptions';
import { RACE_OPTIONS } from '../../constants/raceOptions';
import { MARITAL_STATUS_OPTIONS } from '../../constants/maritalStatusOptions';

// ---------------------------------------------------------------------------
// Reference option sets (mirror backend model choices)
// ---------------------------------------------------------------------------

const CLASSIFICATION_OPTIONS = [
  { value: 'staff', label: 'Staff' },
  { value: 'worker', label: 'Worker' },
  { value: 'intern', label: 'Intern' },
  { value: 'advisor', label: 'Advisor' },
  { value: 'managing_director', label: 'Managing Director' },
  { value: 'executive_director', label: 'Executive Director' },
  { value: 'non_executive_director', label: 'Non-Executive Director' },
  { value: 'outsourced', label: 'Outsourced (JB Office)' },
];

const GENDER_OPTIONS = [
  { value: 'M', label: 'Male' },
  { value: 'F', label: 'Female' },
];

// Singapore Employment Act: a person aged 16+ is an adult worker. Below 16
// requires special MOM handling (medical report, restricted duties), so the
// Add Employee form floors the date of birth at 16 years old.
const MIN_WORKING_AGE_YEARS = 16;
// MOM sets a stricter floor for foreign Work Permit holders: a non-domestic
// migrant worker must be at least 18 years old at the point of application.
const WP_MIN_AGE_YEARS = 18;
// The DOB calendar opens at a typical working-adult birth year rather than
// "today" (which is never a valid DOB for a hire).
const TYPICAL_HIRE_AGE_YEARS = 30;

// Minimum age (years) required for a given pass type.
const minWorkingAgeFor = (passType) =>
  passType === 'wp' ? WP_MIN_AGE_YEARS : MIN_WORKING_AGE_YEARS;

// A Date exactly `years` before today (local time).
const dateYearsAgo = (years) => {
  const d = new Date();
  d.setFullYear(d.getFullYear() - years);
  return d;
};

const EMPLOYMENT_TYPE_OPTIONS = [
  { value: 'full_time', label: 'Full-time' },
  { value: 'part_time', label: 'Part-time' },
];

const PASS_TYPE_OPTIONS = [
  { value: 'citizen', label: 'Singapore Citizen' },
  { value: 'pr', label: 'Permanent Resident (PR)' },
  { value: 'ep', label: 'Employment Pass (EP)' },
  { value: 's_pass', label: 'S Pass' },
  { value: 'wp', label: 'Work Permit (WP)' },
];

const MARITAL_OPTIONS = MARITAL_STATUS_OPTIONS;

const ADDRESS_MODE_OPTIONS = [
  { value: 'residential', label: 'Residential Address' },
  { value: 'dormitory', label: 'Dormitory Address' },
];

const PAY_MODE_OPTIONS = [
  { value: 'giro', label: 'GIRO' },
  { value: 'bank_transfer', label: 'Bank Transfer' },
  { value: 'cheque', label: 'Cheque' },
];

const MOM_CLASS_OPTIONS = [
  { value: 'workman', label: 'Workman' },
  { value: 'non_workman', label: 'Non-workman' },
];

// MOM workman/non-workman is fixed by classification (HR rule):
//   worker -> workman; every office class (staff, intern, advisor,
//   directors) -> non-workman.
const momClassFor = (classification) =>
  classification === 'worker' ? 'workman' : 'non_workman';

// Worker is the only class that does not require a company work email
// (workers authenticate with their staff ID). Everyone else needs one.
const requiresWorkEmail = (classification) =>
  Boolean(classification) && classification !== 'worker';

// Common Singapore banks for the payroll dropdown (typeable for anything else).
const SG_BANK_OPTIONS = [
  'DBS Bank', 'POSB', 'OCBC Bank', 'United Overseas Bank (UOB)',
  'Standard Chartered Bank', 'Maybank', 'HSBC', 'Citibank',
  'CIMB Bank', 'RHB Bank', 'Bank of China', 'ICBC', 'HL Bank',
  'State Bank of India', 'Bank of India',
].map((b) => ({ id: b, name: b }));

// Admin (red) native-checkbox styling. The project does not use
// @tailwindcss/forms, so accent-* is required or the box renders browser-blue.
const CHECKBOX_ADMIN = 'rounded border-gray-300 accent-red-600 text-red-600 focus-visible:ring-red-500';

const NATIONALITY_OPTIONS = [
  'Singaporean', 'Malaysian', 'Bangladeshi', 'Indian', 'Chinese',
  'Filipino', 'Indonesian', 'Myanmar', 'Thai', 'Vietnamese', 'Sri Lankan',
  'Pakistani', 'Nepalese', 'Taiwanese', 'British', 'Australian', 'American',
].map((n) => ({ id: n, name: n }));

const FOREIGN_PASS_TYPES = ['ep', 's_pass', 'wp'];

// NRIC/FIN drives the pass-type universe (HR mental model):
//   NRIC (prefix S/T) -> local  -> Singapore Citizen or PR
//   FIN  (prefix F/G/M) -> foreign -> EP / S Pass / WP
const PASS_TYPES_LOCAL = ['citizen', 'pr'];
const PASS_TYPES_FOREIGN = ['ep', 's_pass', 'wp'];
const NRIC_FIN_REGEX = /^[STFGM]\d{7}[A-Z]$/;

// MOM housing rule: only Work Permit holders may be housed in a migrant-worker
// dormitory (and they may alternatively live in approved residential housing).
// Citizens, PRs, EP and S Pass holders are not dormitory-eligible — they use a
// residential address. (Ref: MOM "migrant worker housing" framework, which sits
// entirely under the Work Permit pass type.)
const dormEligible = (passType) => passType === 'wp';

// 'nric' | 'fin' | null (unknown / incomplete)
const idTypeFromNricFin = (value) => {
  const v = (value || '').trim().toUpperCase();
  if (!v) return null;
  const first = v[0];
  if (first === 'S' || first === 'T') return 'nric';
  if (first === 'F' || first === 'G' || first === 'M') return 'fin';
  return null;
};

const TABS = [
  { key: 'role', label: 'Role & Employment', icon: Briefcase },
  { key: 'identity', label: 'Identity & Residency', icon: User },
  { key: 'immigration', label: 'Pass Documents', icon: Globe },
  { key: 'address', label: 'Address', icon: MapPin },
  { key: 'compensation', label: 'Compensation', icon: CreditCard },
  { key: 'review', label: 'Review & Confirm', icon: ClipboardCheck },
];

// Outsourced (JB Office) holders are not employees — no payroll, no pass
// documents, no address/compensation. The wizard collapses to a single
// minimal-details step plus review.
const OUTSOURCED_TABS = [
  { key: 'details', label: 'Details', icon: Globe },
  { key: 'review', label: 'Review & Confirm', icon: ClipboardCheck },
];

const INITIAL_FORM = {
  // Role & Employment
  classification: '',
  department_id: '',
  trade_category_id: '',
  designation: '',
  reporting_officer_staff_id: '',
  employment_type: 'full_time',
  work_schedule_template_id: '',
  employment_start_date: '',
  probation_months: 3,
  // Personal & Identity
  first_name: '',
  last_name: '',
  date_of_birth: '',
  gender: '',
  nationality: '',
  country_of_birth: '',
  nric_fin: '',
  race: '',
  religion: '',
  marital_status: '',
  marriage_date: '',
  marriage_certificate_number: '',
  personal_email: '',
  personal_mobile: '',
  work_phone_number: '',
  did_number: '',
  extension_number: '',
  blood_type: '',
  // Immigration & Pass
  pass_type: '',
  mom_designation: '',
  pr_obtained_date: '',
  wp_number: '',
  wp_application_date: '',
  pass_issue_date: '',
  pass_expiry_date: '',
  passport_number: '',
  passport_expiry_date: '',
  pass_renewal_reminder_days: 90,
  // Address
  address_mode: 'residential',
  residential_address: '',
  residential_postal_code: '',
  residential_unit_number: '',
  residential_address_location: 'singapore',
  residential_address_country: '',
  dormitory_address: '',
  dormitory_postal_code: '',
  dormitory_unit_number: '',
  // Compensation
  basic_salary: '',
  pay_mode: 'giro',
  bank_name: '',
  bank_account_number: '',
  bank_account_holder: '',
  bank_branch_code: '',
  mom_employment_classification: '',
  work_email: '',
  shg_opt_out: true,
  // Outsourced (JB Office) — minimal, non-payroll record
  full_name: '',
  office_work_site_id: '',
  notes: '',
};

// Maps a server validation field -> tab index so we can jump the user to it.
const FIELD_TAB_INDEX = {
  classification: 0, department_id: 0, trade_category_id: 0, designation: 0,
  reporting_officer_staff_id: 0, employment_type: 0, work_schedule_template_id: 0,
  employment_start_date: 0, probation_months: 0,
  first_name: 1, last_name: 1, date_of_birth: 1, gender: 1, nationality: 1,
  country_of_birth: 1, marital_status: 1,
  nric_fin: 1, pass_type: 1, work_email: 1,
  pr_obtained_date: 2, wp_number: 2, passport_number: 2,
  passport_expiry_date: 2, pass_expiry_date: 2, mom_designation: 2,
  address_mode: 3,
  basic_salary: 4, bank_account_holder: 4,
};

// ---------------------------------------------------------------------------
// Small presentational helpers
// ---------------------------------------------------------------------------

const Field = ({ label, required, hint, error, children, className = '' }) => (
  <div className={className}>
    <label className="form-label-admin flex items-center gap-1">
      {label}
      {required && <span className="text-red-500">*</span>}
    </label>
    {children}
    {hint && !error && <p className="text-xs text-gray-500 mt-1">{hint}</p>}
    {error && (
      <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
        <AlertCircle className="w-3 h-3 flex-shrink-0" />
        {error}
      </p>
    )}
  </div>
);

const SectionNote = ({ children, tone = 'info' }) => {
  const palette = tone === 'warn'
    ? 'bg-amber-50 border-amber-200 text-amber-800'
    : 'bg-blue-50 border-blue-200 text-blue-800';
  return (
    <div className={`flex items-start gap-2 rounded-xl border px-3 py-2 text-xs ${palette}`}>
      <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
      <div>{children}</div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const AddEmployeeModal = ({ isOpen, onClose, onSuccess }) => {
  const [form, setForm] = useState(INITIAL_FORM);
  const [errors, setErrors] = useState({});
  const [activeTab, setActiveTab] = useState(0);
  const [showAllRos, setShowAllRos] = useState(false);
  const [dormPortalId, setDormPortalId] = useState('');

  const directHire = useDirectHire();
  const registerExternal = useRegisterExternalPersonnel();

  // Outsourced (JB Office) holders drive a collapsed, minimal wizard.
  const isOutsourced = form.classification === 'outsourced';
  const currentTabs = isOutsourced ? OUTSOURCED_TABS : TABS;

  const { data: departments = [] } = useDepartments();
  const { data: tradeCategories = [] } = useTradeCategories();
  const { data: workSites = [] } = useExternalWorkSites();
  const staffListQuery = useStaffList({ page_size: 1000 }, { enabled: isOpen });
  const allStaff = staffListQuery.data?.results || [];

  const { data: scheduleTemplates = [] } = useQuery({
    queryKey: ['config', 'work-schedule-templates'],
    queryFn: async () => {
      const res = await staffAPI.getWorkScheduleTemplates();
      return res.data?.results || res.data || [];
    },
    staleTime: 30 * 60 * 1000,
    enabled: isOpen,
  });

  const selectedDept = useMemo(
    () => departments.find((d) => String(d.id) === String(form.department_id)),
    [departments, form.department_id],
  );
  const deptUsesTrades = !!selectedDept?.uses_trade_categories;
  // Trade categories are construction trades performed by workers only. Office
  // staff (and interns / advisors / directors) in a trade department do not
  // carry a trade, so the selector only applies to workers.
  const tradeCategoryApplies = deptUsesTrades && form.classification === 'worker';

  // Scoped designations (department + classification).
  const { data: scopedDesignations = [] } = useQuery({
    queryKey: ['config', 'distinct-designations', form.department_id, form.classification],
    queryFn: async () => {
      const params = {};
      if (form.department_id) params.department = form.department_id;
      if (form.classification) params.classification = form.classification;
      const res = await staffAPI.getDistinctDesignations(params);
      return res.data || [];
    },
    enabled: isOpen && !!form.department_id,
    staleTime: 2 * 60 * 1000,
  });

  // Dormitories from the Dorm Portal (address source of truth). Pulled lazily
  // when the address mode is dormitory; degrades gracefully if the portal is down.
  const { data: portalDorms = [], isError: dormsError } = useQuery({
    queryKey: ['dormitory-portal', 'overview'],
    queryFn: async () => {
      const res = await dormitoryAPI.getDormitoryPortalOverview();
      const payload = res.data || {};
      return Array.isArray(payload.dorms) ? payload.dorms : [];
    },
    enabled: isOpen && form.address_mode === 'dormitory',
    staleTime: 10 * 60 * 1000,
    retry: false,
  });

  // MOM designation titles for the chosen pass type (WP / S Pass = curated list,
  // EP = values learned over time from free-text entries).
  const { data: momDesignations = [] } = useMOMDesignations(form.pass_type, { enabled: isOpen });

  const momDesignationOptions = useMemo(
    () => momDesignations.map((d) => ({
      value: d.designation_name,
      label: d.designation_name,
    })),
    [momDesignations],
  );

  // EP uses a creatable combobox (type-a-new-one + pick-a-suggestion), so it
  // needs id/name items rather than value/label options.
  const momDesignationItems = useMemo(
    () => momDesignations.map((d) => ({ id: d.designation_name, name: d.designation_name })),
    [momDesignations],
  );

  // Staff-id preview for the chosen classification.
  const { data: nextStaffId } = useQuery({
    queryKey: ['staff', 'next-staff-id', form.classification],
    queryFn: async () => {
      const res = await staffAPI.getNextStaffId(form.classification);
      return res.data?.next_staff_id || '';
    },
    enabled: isOpen && !!form.classification,
    staleTime: 30 * 1000,
  });

  // Reset everything when the modal closes.
  useEffect(() => {
    if (!isOpen) {
      setForm(INITIAL_FORM);
      setErrors({});
      setActiveTab(0);
      setShowAllRos(false);
      setDormPortalId('');
    }
  }, [isOpen]);

  const update = useCallback((patch) => {
    setForm((prev) => ({ ...prev, ...patch }));
  }, []);

  const clearError = useCallback((field) => {
    setErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, []);

  const setField = useCallback((field, value) => {
    update({ [field]: value });
    clearError(field);
  }, [update, clearError]);

  // Smart defaults when classification changes.
  const onClassificationChange = (value) => {
    const patch = { classification: value };
    // Reset designation (scope changes) but keep department.
    patch.designation = '';
    if (value === 'outsourced') {
      // Outsourced (JB Office) holders skip pass type, work schedule, trade
      // category and MOM classification entirely — they are not employees.
      patch.pass_type = '';
      patch.address_mode = 'residential';
      patch.trade_category_id = '';
      patch.work_schedule_template_id = '';
      patch.mom_employment_classification = '';
    } else {
      patch.mom_employment_classification = momClassFor(value);
      if (value === 'worker') {
        patch.pass_type = form.pass_type || 'wp';
        patch.address_mode = 'dormitory';
        const sixDay = pickTemplate(scheduleTemplates, '6');
        if (sixDay) patch.work_schedule_template_id = sixDay.id;
      } else if (value) {
        // Every office class (staff, intern, advisor, directors) shares the
        // same defaults: local pass, residential address, 5-day work week.
        patch.pass_type = form.pass_type || 'citizen';
        patch.address_mode = 'residential';
        const fiveDay = pickTemplate(scheduleTemplates, '5-Day');
        if (fiveDay) patch.work_schedule_template_id = fiveDay.id;
        // Trade categories apply to workers only — drop any stale selection.
        patch.trade_category_id = '';
      }
    }
    update(patch);
    clearError('classification');
    clearError('trade_category_id');
  };

  // When department changes, clear designation/trade/RO that no longer apply.
  const onDepartmentChange = (value) => {
    update({
      department_id: value,
      designation: '',
      trade_category_id: '',
      reporting_officer_staff_id: '',
    });
    clearError('department_id');
  };

  // RO list scoped to selected department (with an expand-all toggle).
  // Only active employees can be reporting officers — exclude system accounts
  // and anyone deactivated/terminated (is_active === false or status inactive).
  const departmentRos = useMemo(() => {
    const active = allStaff.filter((s) => (
      s.staff_id
      && s.employment_classification !== 'system'
      && s.is_active !== false
      && s.employment_status !== 'inactive'
    ));
    if (showAllRos || !selectedDept) return active;
    return active.filter((s) => {
      const dep = s.department;
      if (!dep) return false;
      if (typeof dep === 'object') return String(dep.id) === String(selectedDept.id);
      return dep === selectedDept.department_name;
    });
  }, [allStaff, selectedDept, showAllRos]);

  const roItems = useMemo(
    () => departmentRos.map((s) => ({
      id: s.staff_id,
      name: `${displayName(s)} (${s.staff_id})`,
    })),
    [departmentRos],
  );

  const designationItems = useMemo(
    () => scopedDesignations.map((d) => ({ id: d, name: d })),
    [scopedDesignations],
  );

  const dormItems = useMemo(
    () => portalDorms
      .filter((d) => d.dorm_id != null)
      .map((d) => ({ id: String(d.dorm_id), name: d.dorm_name || d.address || String(d.dorm_id) })),
    [portalDorms],
  );

  // Office/work sites for outsourced (JB Office) holders (e.g. the JB site).
  const workSiteItems = useMemo(
    () => workSites.map((s) => ({
      id: s.id,
      name: `${s.site_name}${s.project_code ? ` (${s.project_code})` : ''}`,
    })),
    [workSites],
  );

  const onDormSelect = (v) => {
    setDormPortalId(v);
    const d = portalDorms.find((x) => String(x.dorm_id) === String(v));
    if (d && d.address) {
      update({ dormitory_address: d.address });
      toast.success('Dormitory address filled from the Dorm Portal.');
    }
  };

  const isForeign = FOREIGN_PASS_TYPES.includes(form.pass_type);

  // Residency / pass type is the anchor decision (chosen at the top of the
  // Identity step, pre-defaulted from the classification). It dictates which ID
  // document the person carries, so the NRIC/FIN field adapts to it instead of
  // the two questions depending on each other:
  //   local pass (citizen / PR)   -> NRIC (prefix S/T)
  //   foreign pass (EP / SP / WP) -> FIN  (prefix F/G/M)
  const expectedIdType = PASS_TYPES_LOCAL.includes(form.pass_type)
    ? 'nric'
    : PASS_TYPES_FOREIGN.includes(form.pass_type)
      ? 'fin'
      : null;
  const typedIdType = idTypeFromNricFin(form.nric_fin);
  // True only when the typed ID clearly contradicts the chosen pass type.
  const idMismatch = Boolean(expectedIdType && typedIdType && expectedIdType !== typedIdType);

  const idDocLabel = expectedIdType === 'nric'
    ? 'NRIC'
    : expectedIdType === 'fin' ? 'FIN' : 'NRIC / FIN';
  const idDocPlaceholder = expectedIdType === 'nric'
    ? 'e.g. S1234567A'
    : expectedIdType === 'fin' ? 'e.g. G1234567X' : 'Enter NRIC / FIN';

  // Changing the pass type re-anchors residency. Clear its own error plus any
  // stale NRIC/FIN error (the expectation just changed under it). If the new
  // pass type is not dormitory-eligible, drop any dormitory selection so the
  // address step can only collect a residential address (MOM housing rule).
  const onPassTypeChange = (value) => {
    const patch = { pass_type: value };
    if (!dormEligible(value) && form.address_mode === 'dormitory') {
      patch.address_mode = 'residential';
      patch.dormitory_address = '';
      patch.dormitory_postal_code = '';
      patch.dormitory_unit_number = '';
    }
    // MOM designation applies to WP / S Pass / EP. Drop any stale value when the
    // pass type no longer carries one (local Citizen / PR).
    if (value !== 'wp' && value !== 's_pass' && value !== 'ep') {
      patch.mom_designation = '';
    }
    update(patch);
    clearError('pass_type');
    clearError('nric_fin');
    clearError('mom_designation');
    if (!dormEligible(value)) setDormPortalId('');
  };

  const onNricFinChange = (raw) => {
    setField('nric_fin', (raw || '').toUpperCase());
  };

  // -------------------------------------------------------------------------
  // Validation
  // -------------------------------------------------------------------------

  const validateTab = useCallback((tabIdx, draft = form) => {
    const e = {};
    if (isOutsourced) {
      if (tabIdx === 0) {
        if (!draft.full_name?.trim()) e.full_name = 'Full name is required.';
        if (!draft.work_email?.trim()) {
          e.work_email = 'Work email is required — the asset receipt link is sent here.';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(draft.work_email.trim())) {
          e.work_email = 'Enter a valid email address.';
        }
      }
      return e;
    }
    if (tabIdx === 0) {
      if (!draft.classification) e.classification = 'Select a classification.';
      if (!draft.department_id) e.department_id = 'Select a department.';
      if (deptUsesTrades && draft.classification === 'worker' && !draft.trade_category_id) {
        e.trade_category_id = 'Trade category is required for workers in this department.';
      }
      if (!draft.designation?.trim()) e.designation = 'Designation is required.';
      if (!draft.work_schedule_template_id) e.work_schedule_template_id = 'Select a work schedule.';
      if (!draft.employment_start_date) e.employment_start_date = 'Start date is required.';
    } else if (tabIdx === 1) {
      // Residency / pass type is the anchor for this step.
      if (!draft.pass_type) e.pass_type = 'Select the residency / pass type.';
      if (!draft.first_name?.trim()) e.first_name = 'First name is required.';
      if (!draft.date_of_birth) {
        e.date_of_birth = 'Date of birth is required.';
      } else {
        const dob = parseLocalDate(draft.date_of_birth);
        const minAge = minWorkingAgeFor(draft.pass_type);
        if (dob && dob > dateYearsAgo(minAge)) {
          e.date_of_birth = draft.pass_type === 'wp'
            ? `Work Permit holders must be at least ${minAge} years old (MOM rule).`
            : `Employee must be at least ${minAge} years old.`;
        }
      }
      if (!draft.gender) e.gender = 'Select a gender.';
      if (!draft.nationality?.trim()) e.nationality = 'Nationality is required.';
      if (!draft.marital_status || draft.marital_status === 'not_disclosed') {
        e.marital_status = 'Marital status is required.';
      }
      const idt = idTypeFromNricFin(draft.nric_fin);
      if (!draft.nric_fin?.trim()) {
        e.nric_fin = 'NRIC / FIN is required.';
      } else if (!NRIC_FIN_REGEX.test(draft.nric_fin.trim().toUpperCase())) {
        e.nric_fin = 'Enter a valid NRIC/FIN (e.g. S1234567A or G1234567X).';
      } else if (PASS_TYPES_LOCAL.includes(draft.pass_type) && idt === 'fin') {
        e.nric_fin = 'Singapore Citizens / PRs carry an NRIC (prefix S or T).';
      } else if (PASS_TYPES_FOREIGN.includes(draft.pass_type) && idt === 'nric') {
        e.nric_fin = 'Foreign pass holders carry a FIN (prefix F, G or M).';
      }
      if (requiresWorkEmail(draft.classification) && !draft.work_email?.trim()) {
        e.work_email = 'Work email is required for staff.';
      } else if (draft.work_email?.trim()) {
        const domainErr = workEmailDomainError(draft.work_email, draft.classification);
        if (domainErr) e.work_email = domainErr;
      }
    } else if (tabIdx === 2) {
      if (draft.pass_type === 'pr' && !draft.pr_obtained_date) {
        e.pr_obtained_date = 'PR obtained date is required for PRs.';
      }
      if (draft.pass_type === 'wp' && !draft.wp_number?.trim()) {
        e.wp_number = 'Work Permit number is required.';
      }
      if (FOREIGN_PASS_TYPES.includes(draft.pass_type)) {
        if (!draft.passport_number?.trim()) e.passport_number = 'Passport number is required.';
        if (!draft.passport_expiry_date) e.passport_expiry_date = 'Passport expiry is required.';
        if (!draft.pass_expiry_date) e.pass_expiry_date = 'Pass expiry is required.';
      }
    } else if (tabIdx === 4) {
      if (!draft.basic_salary || Number(draft.basic_salary) <= 0) {
        e.basic_salary = 'Enter a basic salary greater than zero.';
      }
    }
    return e;
  }, [form, deptUsesTrades, isOutsourced]);

  const goNext = () => {
    const e = validateTab(activeTab);
    if (Object.keys(e).length) {
      setErrors((prev) => ({ ...prev, ...e }));
      return;
    }
    setActiveTab((t) => Math.min(t + 1, currentTabs.length - 1));
  };

  const goBack = () => setActiveTab((t) => Math.max(t - 1, 0));

  const handleSubmit = () => {
    if (isOutsourced) {
      const e = validateTab(0);
      if (Object.keys(e).length) {
        setErrors(e);
        setActiveTab(0);
        toast.error('Please complete the highlighted required fields.');
        return;
      }
      const payload = {
        full_name: form.full_name.trim(),
        work_email: form.work_email.trim(),
        office_work_site_id: form.office_work_site_id ? Number(form.office_work_site_id) : null,
        notes: form.notes.trim(),
        designation: form.designation.trim(),
        // Department (configuration.Department) uses a UUID pk — keep as string.
        department_id: form.department_id || null,
        employment_start_date: form.employment_start_date || null,
      };
      registerExternal.mutate(payload, {
        onSuccess: (res) => {
          onSuccess?.(res?.data);
          onClose?.();
        },
      });
      return;
    }

    // Validate every data tab.
    let allErrors = {};
    [0, 1, 2, 4].forEach((idx) => {
      allErrors = { ...allErrors, ...validateTab(idx) };
    });
    if (Object.keys(allErrors).length) {
      setErrors(allErrors);
      const firstField = Object.keys(allErrors)[0];
      const jump = FIELD_TAB_INDEX[firstField];
      if (jump !== undefined) setActiveTab(jump);
      toast.error('Please complete the highlighted required fields.');
      return;
    }

    const payload = buildPayload(form);
    directHire.mutate(payload, {
      onSuccess: (res) => {
        const data = res?.data || {};
        const id = data.staff_id || 'new employee';
        const rejoin = data.hire_type === 'rejoin';
        toast.success(
          rejoin
            ? `Rejoin processed — ${id} re-onboarded.`
            : `${id} created successfully.`,
        );
        onSuccess?.(data);
        onClose?.();
      },
      onError: (err) => {
        const status = err?.response?.status;
        const data = err?.response?.data;
        if (status === 422 && data?.error) {
          toast.error(data.error);
          return;
        }
        if (status === 400 && data && typeof data === 'object') {
          const mapped = {};
          Object.entries(data).forEach(([key, val]) => {
            mapped[key] = Array.isArray(val) ? val.join(' ') : String(val);
          });
          setErrors(mapped);
          const firstField = Object.keys(mapped)[0];
          const jump = FIELD_TAB_INDEX[firstField];
          if (jump !== undefined) setActiveTab(jump);
          toast.error('Please review the highlighted fields.');
          return;
        }
        toast.error('Failed to add employee. Please try again.');
      },
    });
  };

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  if (!isOpen) return null;

  const isSubmitting = isOutsourced ? registerExternal.isPending : directHire.isPending;
  const submitLabel = isOutsourced
    ? (isSubmitting ? 'Registering…' : 'Register Holder')
    : (isSubmitting ? 'Creating…' : 'Create Employee');

  const footer = (
    <div className="flex items-center justify-between w-full gap-3">
      <button type="button" className="btn btn-admin-outline" onClick={onClose}>
        Cancel
      </button>
      <div className="flex items-center gap-3">
        {activeTab > 0 && (
          <button type="button" className="btn btn-admin-secondary" onClick={goBack}>
            <ChevronLeft className="w-4 h-4 mr-1" /> Back
          </button>
        )}
        {activeTab < currentTabs.length - 1 ? (
          <button type="button" className="btn btn-admin-primary" onClick={goNext}>
            Next <ChevronRight className="w-4 h-4 ml-1" />
          </button>
        ) : (
          <button
            type="button"
            className="btn btn-admin-primary"
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            <Plus className="w-4 h-4 mr-2" />
            {submitLabel}
          </button>
        )}
      </div>
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Add Employee"
      size="xl"
      theme="admin"
      footer={footer}
    >
      {/* Stepper — 6 steps fit without horizontal scroll: compact connected
          dots on mobile (the context line names the current step), labeled
          pills on desktop. No overflow-x so the pills are never clipped. */}
      <div className="sticky top-0 z-10 -mx-4 sm:-mx-6 px-4 sm:px-6 bg-white border-b border-gray-100 pb-3 mb-4">
        <p className="text-xs font-medium text-gray-600 mb-2.5 sm:hidden">
          Step {activeTab + 1} of {currentTabs.length}
          <span className="text-gray-400 mx-1">·</span>
          {currentTabs[activeTab].label}
        </p>

        {/* Mobile: connected numbered dots — all steps visible at a glance */}
        <div className="flex items-center sm:hidden">
          {currentTabs.map((tab, idx) => {
            const isActive = idx === activeTab;
            const isDone = idx < activeTab;
            return (
              <React.Fragment key={tab.key}>
                {idx > 0 && (
                  <div className={`h-0.5 flex-1 rounded-full ${idx <= activeTab ? 'bg-red-500' : 'bg-gray-200'}`} />
                )}
                <button
                  type="button"
                  onClick={() => setActiveTab(idx)}
                  aria-label={`${tab.label}${isActive ? ' (current)' : isDone ? ' (completed)' : ''}`}
                  aria-current={isActive ? 'step' : undefined}
                  className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold flex-shrink-0 transition-colors ${
                    isActive
                      ? 'bg-red-600 text-white ring-2 ring-red-200'
                      : isDone
                        ? 'bg-red-100 text-red-700'
                        : 'bg-gray-100 text-gray-400'
                  }`}
                >
                  {isDone ? <Check className="w-3.5 h-3.5" /> : idx + 1}
                </button>
              </React.Fragment>
            );
          })}
        </div>

        {/* Desktop: labeled pills spread evenly across the full modal width */}
        <div className="hidden sm:flex items-center gap-1.5">
          {currentTabs.map((tab, idx) => {
            const isActive = idx === activeTab;
            const isDone = idx < activeTab;
            return (
              <button
                key={tab.key}
                type="button"
                onClick={() => setActiveTab(idx)}
                title={tab.label}
                aria-label={`${tab.label}${isActive ? ' (current)' : isDone ? ' (completed)' : ''}`}
                aria-current={isActive ? 'step' : undefined}
                className={`flex-1 min-w-0 flex items-center justify-center gap-1.5 px-3 py-2 rounded-full text-xs font-medium transition-colors ${
                  isActive
                    ? 'bg-red-600 text-white'
                    : isDone
                      ? 'bg-red-50 text-red-700'
                      : 'text-gray-500 hover:bg-gray-100'
                }`}
              >
                <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[11px] flex-shrink-0 ${
                  isActive ? 'bg-white/20' : isDone ? 'bg-red-100' : 'bg-gray-100'
                }`}>
                  {isDone ? <Check className="w-3 h-3" /> : idx + 1}
                </span>
                <span className="truncate">{tab.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="flex-1">
        {isOutsourced ? (
          <>
            {activeTab === 0 && renderOutsourcedDetailsTab()}
            {activeTab === 1 && renderOutsourcedReviewTab()}
          </>
        ) : (
          <>
            {activeTab === 0 && renderRoleTab()}
            {activeTab === 1 && renderIdentityTab()}
            {activeTab === 2 && renderImmigrationTab()}
            {activeTab === 3 && renderAddressTab()}
            {activeTab === 4 && renderCompensationTab()}
            {activeTab === 5 && renderReviewTab()}
          </>
        )}
      </div>
    </Modal>
  );

  // ---- Tab renderers (closures over state) ----

  function renderRoleTab() {
    return (
      <div className="space-y-5">
        <SectionNote>
          Start with the role. The classification and department drive the rest of the
          form — they scope the designation, reporting officer, and trade category lists.
        </SectionNote>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <Field label="Classification" required error={errors.classification}>
            <FormSelect
              theme="admin"
              options={CLASSIFICATION_OPTIONS}
              value={form.classification}
              onChange={onClassificationChange}
              placeholder="Select classification"
            />
          </Field>
          <Field
            label="Staff ID (auto-assigned)"
            hint="Allocated automatically when you create the employee."
          >
            <div className="input-admin flex items-center bg-gray-50 text-gray-600">
              {form.classification ? (nextStaffId || 'Loading…') : 'Select a classification first'}
            </div>
          </Field>
          <Field label="Department" required error={errors.department_id}>
            <SearchableCombobox
              variant="form"
              theme="admin"
              items={departments}
              value={form.department_id}
              onChange={onDepartmentChange}
              valueFn={(d) => d.id}
              displayFn={(d) => d.department_name}
              placeholder="Select department"
              searchPlaceholder="Search departments…"
            />
          </Field>
          {tradeCategoryApplies && (
            <Field label="Trade Category" required error={errors.trade_category_id}>
              <SearchableCombobox
                variant="form"
                theme="admin"
                items={tradeCategories}
                value={form.trade_category_id}
                onChange={(v) => setField('trade_category_id', v)}
                valueFn={(t) => t.id}
                displayFn={(t) => t.name}
                placeholder="Select trade category"
                searchPlaceholder="Search trades…"
              />
            </Field>
          )}
          <Field
            label="Designation"
            required
            error={errors.designation}
            hint={form.department_id ? 'Pick an existing role or type a new one.' : 'Select a department first.'}
          >
            <SearchableCombobox
              variant="form"
              theme="admin"
              items={designationItems}
              value={form.designation}
              onChange={(v) => setField('designation', v)}
              valueFn={(d) => d.id}
              displayFn={(d) => d.name}
              displayValue={form.designation || undefined}
              placeholder={form.department_id ? 'Select or type designation' : 'Select department first'}
              searchPlaceholder="Search designations…"
              disabled={!form.department_id}
              creatableLabel={(term) => `Use "${term}"`}
              onCreateNew={(term) => term?.trim() && setField('designation', term.trim())}
              emptyLabel="No designations yet — type to add one"
            />
          </Field>
          <Field
            label="Reporting Officer"
            error={errors.reporting_officer_staff_id}
            hint="The direct line supervisor (may differ from the HOD)."
          >
            <SearchableCombobox
              variant="form"
              theme="admin"
              items={roItems}
              value={form.reporting_officer_staff_id}
              onChange={(v) => setField('reporting_officer_staff_id', v)}
              valueFn={(r) => r.id}
              displayFn={(r) => r.name}
              placeholder="Select reporting officer"
              searchPlaceholder="Search staff…"
              clearLabel="No reporting officer"
            />
            <label className="flex items-center gap-2 mt-2 text-xs text-gray-600 cursor-pointer">
              <input
                type="checkbox"
                checked={showAllRos}
                onChange={(e) => setShowAllRos(e.target.checked)}
                className={CHECKBOX_ADMIN}
              />
              Show officers from all departments
            </label>
          </Field>
          <Field label="Employment Type" error={errors.employment_type}>
            <FormSelect
              theme="admin"
              options={EMPLOYMENT_TYPE_OPTIONS}
              value={form.employment_type}
              onChange={(v) => setField('employment_type', v)}
            />
          </Field>
          <Field
            label="Work Schedule Template"
            required
            error={errors.work_schedule_template_id}
            hint="Used for payroll, attendance, rest-day and leave expansion."
          >
            <FormSelect
              theme="admin"
              options={scheduleTemplates.map((t) => ({
                value: t.id,
                label: formatWorkScheduleTemplateLabel(t),
              }))}
              value={form.work_schedule_template_id}
              onChange={(v) => setField('work_schedule_template_id', v)}
              placeholder="Select work schedule"
            />
          </Field>
          <Field label="Employment Start Date" required error={errors.employment_start_date}>
            <DatePickerField
              theme="admin"
              selected={parseLocalDate(form.employment_start_date)}
              onChange={(date) => setField('employment_start_date', date ? toLocalISODate(date) : '')}
              placeholderText="Select start date"
            />
          </Field>
          <Field label="Probation (months)" hint="Defaults to 3 months.">
            <input
              type="number"
              min="0"
              max="24"
              className="input-admin"
              value={form.probation_months}
              onChange={(e) => setField('probation_months', e.target.value === '' ? '' : Number(e.target.value))}
            />
          </Field>
        </div>
      </div>
    );
  }

  function renderIdentityTab() {
    return (
      <div className="space-y-5">
        {/* Residency / pass type is the anchor: it decides whether this person
            carries an NRIC or a FIN and which pass documents are collected in
            the next step. Pre-defaulted from the classification chosen earlier. */}
        <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <Field
              label="Residency / Pass Type"
              required
              error={errors.pass_type}
              hint="Sets whether an NRIC or FIN is expected below and the documents in the next step."
            >
              <FormSelect
                theme="admin"
                options={PASS_TYPE_OPTIONS}
                value={form.pass_type}
                onChange={onPassTypeChange}
                placeholder="Select residency / pass type"
              />
            </Field>
            <Field
              label={idDocLabel}
              required
              error={errors.nric_fin}
              hint={
                expectedIdType === 'nric'
                  ? 'Singapore Citizen / PR — NRIC starts with S or T.'
                  : expectedIdType === 'fin'
                    ? 'Foreign pass holder — FIN starts with F, G or M.'
                    : 'Pick the residency / pass type first; the expected ID adapts.'
              }
            >
              <input
                className="input-admin"
                value={form.nric_fin}
                onChange={(e) => onNricFinChange(e.target.value)}
                placeholder={idDocPlaceholder}
              />
              {idMismatch && (
                <p className="text-xs text-amber-700 mt-1 flex items-center gap-1">
                  <AlertCircle className="w-3 h-3 flex-shrink-0" />
                  {expectedIdType === 'nric'
                    ? 'This looks like a FIN, but a citizen/PR was selected.'
                    : 'This looks like an NRIC, but a foreign pass was selected.'}
                </p>
              )}
            </Field>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <Field label="First / Given Name" required error={errors.first_name}>
            <input
              className="input-admin"
              value={form.first_name}
              onChange={(e) => setField('first_name', e.target.value)}
              placeholder="e.g. Zi Liang Roger"
            />
          </Field>
          <Field label="Last / Family Name" error={errors.last_name}>
            <input
              className="input-admin"
              value={form.last_name}
              onChange={(e) => setField('last_name', e.target.value)}
              placeholder="e.g. Ng"
            />
          </Field>
          <Field
            label="Date of Birth"
            required
            error={errors.date_of_birth}
            hint={
              form.pass_type === 'wp'
                ? `Work Permit holders must be at least ${WP_MIN_AGE_YEARS} years old (MOM rule).`
                : `Employee must be at least ${MIN_WORKING_AGE_YEARS} years old.`
            }
          >
            <DatePickerField
              theme="admin"
              selected={parseLocalDate(form.date_of_birth)}
              onChange={(date) => setField('date_of_birth', date ? toLocalISODate(date) : '')}
              placeholderText="Select date of birth"
              maxDate={dateYearsAgo(minWorkingAgeFor(form.pass_type))}
              openToDate={parseLocalDate(form.date_of_birth) || dateYearsAgo(TYPICAL_HIRE_AGE_YEARS)}
            />
          </Field>
          <Field label="Gender" required error={errors.gender}>
            <FormSelect
              theme="admin"
              options={GENDER_OPTIONS}
              value={form.gender}
              onChange={(v) => setField('gender', v)}
              placeholder="Select gender"
            />
          </Field>
          <Field label="Nationality" required error={errors.nationality}>
            <SearchableCombobox
              variant="form"
              theme="admin"
              items={NATIONALITY_OPTIONS}
              value={form.nationality}
              onChange={(v) => setField('nationality', v)}
              valueFn={(n) => n.id}
              displayFn={(n) => n.name}
              displayValue={form.nationality || undefined}
              placeholder="Select or type nationality"
              searchPlaceholder="Search nationality…"
              creatableLabel={(term) => `Use "${term}"`}
              onCreateNew={(term) => term?.trim() && setField('nationality', term.trim())}
            />
          </Field>
          <Field label="Country/Region of Birth" error={errors.country_of_birth}>
            <CountryOfBirthField
              theme="admin"
              hideLabel
              helpText=""
              isEditing
              value={form.country_of_birth}
              onChange={(v) => setField('country_of_birth', v || '')}
            />
          </Field>
          <Field label="Race" error={errors.race}>
            <FormSelect
              theme="admin"
              options={RACE_OPTIONS}
              value={form.race}
              onChange={(v) => setField('race', v)}
              placeholder="Select race (optional)"
            />
          </Field>
          <Field label="Religion" error={errors.religion}>
            <FormSelect
              theme="admin"
              options={RELIGION_OPTIONS}
              value={form.religion}
              onChange={(v) => setField('religion', v)}
              placeholder="Select religion (optional)"
            />
          </Field>
          <Field label="Marital Status" required error={errors.marital_status}>
            <FormSelect
              theme="admin"
              options={MARITAL_OPTIONS}
              value={form.marital_status}
              onChange={(v) => setField('marital_status', v)}
              placeholder="Select marital status"
            />
          </Field>
          {form.marital_status === 'married' && (
            <>
              <Field label="Marriage Date" hint="Used for paternity leave eligibility.">
                <DatePickerField
                  theme="admin"
                  selected={parseLocalDate(form.marriage_date)}
                  onChange={(date) => setField('marriage_date', date ? toLocalISODate(date) : '')}
                  placeholderText="Select marriage date"
                />
              </Field>
              <Field label="Marriage Certificate No.">
                <input
                  className="input-admin"
                  value={form.marriage_certificate_number}
                  onChange={(e) => setField('marriage_certificate_number', e.target.value)}
                  placeholder="Certificate number"
                />
              </Field>
            </>
          )}
          <Field label="Personal Email" error={errors.personal_email}>
            <input
              type="email"
              className="input-admin"
              value={form.personal_email}
              onChange={(e) => setField('personal_email', e.target.value)}
              placeholder="name@example.com"
            />
          </Field>
          <Field label="Personal Mobile" error={errors.personal_mobile}>
            <input
              type="tel"
              className="input-admin"
              value={form.personal_mobile}
              onChange={(e) => setField('personal_mobile', e.target.value)}
              placeholder="e.g. 90000000"
            />
          </Field>
          <Field
            label="Work Email"
            required={requiresWorkEmail(form.classification)}
            error={errors.work_email}
            hint={
              form.classification === 'worker'
                ? 'Optional — workers log in with their staff ID. If set, must be @winner-e.sg.'
                : `Company mailbox only (@${COMPANY_EMAIL_DOMAIN}). Personal email goes in Personal Email.`
            }
          >
            <input
              type="email"
              className="input-admin"
              value={form.work_email}
              onChange={(e) => setField('work_email', e.target.value)}
              placeholder={`name@${COMPANY_EMAIL_DOMAIN}`}
            />
          </Field>
          <Field
            label="Work Number"
            error={errors.work_phone_number}
            hint="Company mobile. Desk direct lines go in Direct line."
          >
            <input
              type="tel"
              className="input-admin"
              value={form.work_phone_number}
              onChange={(e) => setField('work_phone_number', e.target.value)}
              placeholder="e.g. 9123 4567"
            />
          </Field>
          <Field
            label="Direct line"
            error={errors.did_number}
            hint="Office DID, usually 6xxx. Kept when a company mobile is assigned."
          >
            <input
              type="tel"
              className="input-admin"
              value={form.did_number}
              onChange={(e) => setField('did_number', e.target.value)}
              placeholder="e.g. 6481 8423"
            />
          </Field>
          <Field
            label="Extension"
            error={errors.extension_number}
            hint="Rings after the direct line. Not part of the mobile."
          >
            <input
              className="input-admin"
              value={form.extension_number}
              onChange={(e) => setField('extension_number', e.target.value)}
              placeholder="e.g. 201"
            />
          </Field>
        </div>
      </div>
    );
  }

  function renderImmigrationTab() {
    return (
      <div className="space-y-5">
        {!form.pass_type ? (
          <SectionNote tone="warn">
            Select the residency / pass type in the <strong>Identity &amp; Residency</strong>{' '}
            step first — the documents required here depend on it.
          </SectionNote>
        ) : (
          <SectionNote>
            Pass type: <strong>{labelOf(PASS_TYPE_OPTIONS, form.pass_type)}</strong>{' '}
            (set in Identity &amp; Residency).{' '}
            {PASS_TYPES_LOCAL.includes(form.pass_type)
              ? (form.pass_type === 'pr'
                ? 'PRs only need the date PR was obtained.'
                : 'Singapore Citizens have no immigration documents to record — continue to the next step.')
              : 'Provide the passport and pass details below.'}
          </SectionNote>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {form.pass_type === 'pr' && (
            <Field label="PR Obtained Date" required error={errors.pr_obtained_date}>
              <DatePickerField
                theme="admin"
                selected={parseLocalDate(form.pr_obtained_date)}
                onChange={(date) => setField('pr_obtained_date', date ? toLocalISODate(date) : '')}
                placeholderText="Select PR obtained date"
                maxDate={new Date()}
              />
            </Field>
          )}
          {form.pass_type === 'wp' && (
            <>
              <Field label="Work Permit Number" required error={errors.wp_number}>
                <input
                  className="input-admin"
                  value={form.wp_number}
                  onChange={(e) => setField('wp_number', e.target.value)}
                  placeholder="e.g. 0 1234567"
                />
              </Field>
              <Field label="WP Application Date">
                <DatePickerField
                  theme="admin"
                  selected={parseLocalDate(form.wp_application_date)}
                  onChange={(date) => setField('wp_application_date', date ? toLocalISODate(date) : '')}
                  placeholderText="Select application date"
                />
              </Field>
            </>
          )}
          {(form.pass_type === 'wp' || form.pass_type === 's_pass' || form.pass_type === 'ep') && (
            <Field
              label="MOM Designation"
              error={errors.mom_designation}
              hint={
                form.pass_type === 'ep'
                  ? 'Official MOM-reported designation. Type a new one or pick a previously-used value — new entries are saved for future use.'
                  : 'Official MOM-reported designation from the pass — may differ from the internal designation in Step 1. Optional.'
              }
              className="md:col-span-2"
            >
              {form.pass_type === 'ep' ? (
                <SearchableCombobox
                  variant="form"
                  theme="admin"
                  items={momDesignationItems}
                  value={form.mom_designation}
                  onChange={(v) => setField('mom_designation', v)}
                  valueFn={(d) => d.id}
                  displayFn={(d) => d.name}
                  displayValue={form.mom_designation || undefined}
                  placeholder="Select or type MOM designation"
                  searchPlaceholder="Search designations…"
                  creatableLabel={(term) => `Use "${term}"`}
                  onCreateNew={(term) => term?.trim() && setField('mom_designation', term.trim())}
                  emptyLabel="No saved designations yet — type to add one"
                  clearLabel="Clear"
                />
              ) : (
                <FormSelect
                  theme="admin"
                  options={momDesignationOptions}
                  value={form.mom_designation}
                  onChange={(v) => setField('mom_designation', v)}
                  placeholder="Select MOM designation"
                />
              )}
            </Field>
          )}
          {isForeign && (
            <>
              <Field label="Passport Number" required error={errors.passport_number}>
                <input
                  className="input-admin"
                  value={form.passport_number}
                  onChange={(e) => setField('passport_number', e.target.value)}
                  placeholder="Passport number"
                />
              </Field>
              <Field label="Passport Expiry" required error={errors.passport_expiry_date}>
                <DatePickerField
                  theme="admin"
                  selected={parseLocalDate(form.passport_expiry_date)}
                  onChange={(date) => setField('passport_expiry_date', date ? toLocalISODate(date) : '')}
                  placeholderText="Select passport expiry"
                />
              </Field>
              <Field label="Pass Issue Date">
                <DatePickerField
                  theme="admin"
                  selected={parseLocalDate(form.pass_issue_date)}
                  onChange={(date) => setField('pass_issue_date', date ? toLocalISODate(date) : '')}
                  placeholderText="Select pass issue date"
                />
              </Field>
              <Field label="Pass Expiry" required error={errors.pass_expiry_date}>
                <DatePickerField
                  theme="admin"
                  selected={parseLocalDate(form.pass_expiry_date)}
                  onChange={(date) => setField('pass_expiry_date', date ? toLocalISODate(date) : '')}
                  placeholderText="Select pass expiry"
                />
              </Field>
              <Field label="Renewal Reminder (days)" hint="Lead time before expiry to flag for renewal.">
                <input
                  type="number"
                  min="0"
                  className="input-admin"
                  value={form.pass_renewal_reminder_days}
                  onChange={(e) => setField('pass_renewal_reminder_days', e.target.value === '' ? '' : Number(e.target.value))}
                />
              </Field>
            </>
          )}
        </div>
      </div>
    );
  }

  function renderAddressTab() {
    const eligible = dormEligible(form.pass_type);
    const isDorm = eligible && form.address_mode === 'dormitory';
    return (
      <div className="space-y-5">
        <SectionNote>
          Address is optional at this stage — you can save the employee now and
          complete it later from their profile.
        </SectionNote>
        {eligible ? (
          <Field label="Address Type" hint="Work Permit holders may be housed in a dormitory or a residential address.">
            <FormSelect
              theme="admin"
              options={ADDRESS_MODE_OPTIONS}
              value={form.address_mode}
              onChange={(v) => setField('address_mode', v)}
            />
          </Field>
        ) : (
          <SectionNote tone="warn">
            Per MOM housing rules, only Work Permit holders may be housed in a
            dormitory.{' '}
            {form.pass_type
              ? `${labelOf(PASS_TYPE_OPTIONS, form.pass_type)} holders use a residential address.`
              : 'This pass type uses a residential address.'}
          </SectionNote>
        )}
        {isDorm && (
          <Field
            label="Dormitory"
            hint={
              dormsError
                ? 'Dorm Portal unavailable — enter the address manually below.'
                : 'Pick a dormitory to auto-fill its address from the Dorm Portal.'
            }
          >
            <SearchableCombobox
              variant="form"
              theme="admin"
              items={dormItems}
              value={dormPortalId}
              onChange={onDormSelect}
              valueFn={(d) => d.id}
              displayFn={(d) => d.name}
              placeholder={dormItems.length ? 'Select dormitory' : 'No dormitories available — enter manually'}
              searchPlaceholder="Search dormitories…"
              disabled={!dormItems.length}
              clearLabel="Clear selection"
            />
          </Field>
        )}
        <div className="space-y-4">
          {/* Residential: Singapore | Overseas (shared editor).
              Dormitory: Dorm Portal selection is source of truth + manual fallback. */}
          {!isDorm ? (
            <ResidentialAddressEditor
              isEditing
              formData={form}
              setFormData={(updater) => {
                setForm((prev) =>
                  typeof updater === 'function' ? updater(prev) : { ...prev, ...updater }
                );
              }}
              onChangeField={setField}
              theme="admin"
              audience="hr"
              showAddressTypeBadge={false}
              fieldKeys={{
                address: 'residential_address',
                postal: 'residential_postal_code',
                unit: 'residential_unit_number',
                location: 'residential_address_location',
                country: 'residential_address_country',
              }}
            />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Field
                label="Dormitory Address"
                className="md:col-span-2"
                hint={
                  dormsError || !dormItems.length
                    ? 'Dorm Portal unavailable — enter the address manually.'
                    : 'Auto-filled from the selected dormitory.'
                }
              >
                <input
                  className="input-admin"
                  value={form.dormitory_address}
                  onChange={(e) => setField('dormitory_address', e.target.value)}
                  placeholder="Select a dormitory above to auto-fill"
                />
              </Field>
              <Field label="Room / Unit Number">
                <input
                  className="input-admin"
                  value={form.dormitory_unit_number}
                  onChange={(e) => setField('dormitory_unit_number', e.target.value)}
                  placeholder="e.g. #01-23"
                />
              </Field>
            </div>
          )}
        </div>
      </div>
    );
  }

  function renderCompensationTab() {
    return (
      <div className="space-y-5">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <Field label="Basic Salary (SGD / month)" required error={errors.basic_salary}>
            <input
              type="number"
              min="0"
              step="0.01"
              className="input-admin"
              value={form.basic_salary}
              onChange={(e) => setField('basic_salary', e.target.value)}
              placeholder="e.g. 2000"
            />
          </Field>
          <Field label="Pay Mode" error={errors.pay_mode}>
            <FormSelect
              theme="admin"
              options={PAY_MODE_OPTIONS}
              value={form.pay_mode}
              onChange={(v) => setField('pay_mode', v)}
            />
          </Field>
          <Field label="Bank Name">
            <SearchableCombobox
              variant="form"
              theme="admin"
              items={SG_BANK_OPTIONS}
              value={form.bank_name}
              onChange={(v) => setField('bank_name', v)}
              valueFn={(b) => b.id}
              displayFn={(b) => b.name}
              displayValue={form.bank_name || undefined}
              placeholder="Select bank"
              searchPlaceholder="Search banks…"
              creatableLabel={(term) => `Use "${term}"`}
              onCreateNew={(term) => term?.trim() && setField('bank_name', term.trim())}
              clearLabel="Clear"
            />
          </Field>
          <Field label="Bank Account Number">
            <input
              className="input-admin"
              value={form.bank_account_number}
              onChange={(e) => setField('bank_account_number', e.target.value)}
              placeholder="Account number"
            />
          </Field>
          <Field
            label="Account Holder Name"
            hint="Enter the name exactly as registered with the bank. HR verification is still required."
          >
            <input
              className="input-admin"
              value={form.bank_account_holder}
              onChange={(e) => setField('bank_account_holder', e.target.value)}
              placeholder="Name as shown on bank statement"
            />
          </Field>
          <Field label="Bank Branch Code" hint="Free text — branches vary by bank (often part of the account number).">
            <input
              className="input-admin"
              value={form.bank_branch_code}
              onChange={(e) => setField('bank_branch_code', e.target.value)}
              placeholder="Branch code (optional)"
            />
          </Field>
          <Field
            label="MOM Classification"
            hint="Set automatically from the classification (worker → workman, staff → non-workman)."
          >
            <div className="input-admin flex items-center bg-gray-50 text-gray-700">
              {form.classification
                ? labelOf(MOM_CLASS_OPTIONS, momClassFor(form.classification))
                : 'Select a classification first'}
            </div>
          </Field>
        </div>
        <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
          <input
            type="checkbox"
            checked={form.shg_opt_out}
            onChange={(e) => setField('shg_opt_out', e.target.checked)}
            className={CHECKBOX_ADMIN}
          />
          Opt out of SHG community fund donations (CDAC / SINDA / MBMF / ECF)
        </label>
      </div>
    );
  }

  function renderOutsourcedDetailsTab() {
    return (
      <div className="space-y-5">
        <SectionNote>
          Outsourced holders (e.g. the JB office) are <strong>not</strong> company
          employees — no payroll, no headcount, and no portal login. This creates a
          minimal record so company assets can be assigned to them; manage it going
          forward from <strong>Asset Admin → External Personnel</strong>.
        </SectionNote>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <Field label="Classification" required error={errors.classification}>
            <FormSelect
              theme="admin"
              options={CLASSIFICATION_OPTIONS}
              value={form.classification}
              onChange={onClassificationChange}
              placeholder="Select classification"
            />
          </Field>
          <Field
            label="Staff ID (auto-assigned)"
            hint="Allocated automatically when you create the record."
          >
            <div className="input-admin flex items-center bg-gray-50 text-gray-600">
              {nextStaffId || 'Loading…'}
            </div>
          </Field>
          <Field label="Full Name" required error={errors.full_name}>
            <input
              className="input-admin"
              value={form.full_name}
              onChange={(e) => setField('full_name', e.target.value)}
              placeholder="e.g. Ahmad bin Ismail"
            />
          </Field>
          <Field
            label="Work Email"
            required
            error={errors.work_email}
            hint="The asset receipt link is emailed here."
          >
            <input
              type="email"
              className="input-admin"
              value={form.work_email}
              onChange={(e) => setField('work_email', e.target.value)}
              placeholder="name@example.com"
            />
          </Field>
          <Field label="Department" hint="Optional — for reference only.">
            <SearchableCombobox
              variant="form"
              theme="admin"
              items={departments}
              value={form.department_id}
              onChange={(v) => setField('department_id', v)}
              valueFn={(d) => d.id}
              displayFn={(d) => d.department_name}
              placeholder="Select department (optional)"
              searchPlaceholder="Search departments…"
              clearLabel="No department"
            />
          </Field>
          <Field label="Designation" hint="Optional — free-text role/title.">
            <input
              className="input-admin"
              value={form.designation}
              onChange={(e) => setField('designation', e.target.value)}
              placeholder="e.g. Site Assistant"
            />
          </Field>
          <Field label="Employment Start Date" hint="Optional — record-keeping only.">
            <DatePickerField
              theme="admin"
              selected={parseLocalDate(form.employment_start_date)}
              onChange={(date) => setField('employment_start_date', date ? toLocalISODate(date) : '')}
              placeholderText="Select start date"
            />
          </Field>
          <Field label="Office / Work Site" hint="Optional — e.g. the JB site.">
            <SearchableCombobox
              variant="form"
              theme="admin"
              items={workSiteItems}
              value={form.office_work_site_id}
              onChange={(v) => setField('office_work_site_id', v)}
              valueFn={(s) => s.id}
              displayFn={(s) => s.name}
              placeholder="Select work site (optional)"
              searchPlaceholder="Search work sites…"
              clearLabel="No work site"
            />
          </Field>
          <Field label="Notes" className="md:col-span-2" hint="Optional, non-PDPA-sensitive notes.">
            <textarea
              className="input-admin w-full resize-none"
              rows={2}
              value={form.notes}
              onChange={(e) => setField('notes', e.target.value)}
              placeholder="Optional notes"
            />
          </Field>
        </div>
      </div>
    );
  }

  function renderOutsourcedReviewTab() {
    const site = workSiteItems.find((s) => String(s.id) === String(form.office_work_site_id));
    const rows = [
      ['Staff ID (preview)', nextStaffId || '—'],
      ['Classification', 'Outsourced (JB Office)'],
      ['Full Name', form.full_name || '—'],
      ['Work Email', form.work_email || '—'],
      ['Department', selectedDept?.department_name || '—'],
      ['Designation', form.designation || '—'],
      ['Start Date', form.employment_start_date || '—'],
      ['Office / Work Site', site?.name || '—'],
    ];
    return (
      <div className="space-y-4">
        <SectionNote>
          This creates a minimal, non-payroll record with no portal login. Manage it
          going forward from <strong>Asset Admin → External Personnel</strong>.
        </SectionNote>
        <div className="rounded-2xl border border-gray-200 divide-y divide-gray-100 overflow-hidden">
          {rows.map(([k, v]) => (
            <div key={k} className="flex items-center justify-between px-4 py-2.5 text-sm">
              <span className="text-gray-500">{k}</span>
              <span className="font-medium text-gray-900 text-right">{v}</span>
            </div>
          ))}
        </div>
      </div>
    );
  }

  function renderReviewTab() {
    const startDate = parseLocalDate(form.employment_start_date);
    const isFuture = startDate && startDate > new Date(new Date().toDateString());
    const rows = [
      ['Staff ID (preview)', nextStaffId || '—'],
      ['Name', `${form.first_name} ${form.last_name}`.trim() || '—'],
      ['Classification', labelOf(CLASSIFICATION_OPTIONS, form.classification)],
      ['Department', selectedDept?.department_name || '—'],
      ['Designation', form.designation || '—'],
      ...(form.mom_designation ? [['MOM Designation', form.mom_designation]] : []),
      ['Reporting Officer', form.reporting_officer_staff_id || '—'],
      ['Pass Type', labelOf(PASS_TYPE_OPTIONS, form.pass_type)],
      ['Start Date', form.employment_start_date || '—'],
      ['Basic Salary', form.basic_salary ? `$${form.basic_salary}` : '—'],
      ['Work Email', form.work_email || (form.classification === 'worker' ? '(staff ID login)' : '—')],
      ['Work Number', form.work_phone_number || '—'],
      ['Direct line', form.did_number ? `${form.did_number}${form.extension_number ? ` ext. ${form.extension_number}` : ''}` : '—'],
    ];
    return (
      <div className="space-y-4">
        <SectionNote tone={isFuture ? 'warn' : 'info'}>
          {isFuture
            ? 'This is a future start date. The employee will be created as Pre-Onboarding and will only become active (counted in headcount) on their start date.'
            : 'Review the details below, then create the employee. All records (profile, leave balances, schedule, salary) are created atomically.'}
        </SectionNote>
        <div className="rounded-2xl border border-gray-200 divide-y divide-gray-100 overflow-hidden">
          {rows.map(([k, v]) => (
            <div key={k} className="flex items-center justify-between px-4 py-2.5 text-sm">
              <span className="text-gray-500">{k}</span>
              <span className="font-medium text-gray-900 text-right">{v}</span>
            </div>
          ))}
        </div>
      </div>
    );
  }
};

// ---------------------------------------------------------------------------
// Pure helpers
// ---------------------------------------------------------------------------

function displayName(s) {
  const alias = (s.alias || '').trim();
  if (alias) return alias;
  return [s.first_name, s.last_name].filter(Boolean).join(' ') || s.staff_id;
}

function pickTemplate(templates, needle) {
  return templates.find((t) => {
    const hay = `${t.name || ''} ${t.schedule_type_display || ''}`.toLowerCase();
    return hay.includes(needle.toLowerCase());
  });
}

function labelOf(options, value) {
  return options.find((o) => o.value === value)?.label || '—';
}

function buildPayload(form) {
  const payload = {
    classification: form.classification,
    department_id: form.department_id,
    designation: form.designation.trim(),
    employment_type: form.employment_type,
    work_schedule_template_id: form.work_schedule_template_id || null,
    employment_start_date: form.employment_start_date,
    probation_months: form.probation_months === '' ? 3 : Number(form.probation_months),
    first_name: form.first_name.trim(),
    last_name: form.last_name.trim(),
    date_of_birth: form.date_of_birth,
    gender: form.gender,
    nationality: form.nationality.trim(),
    country_of_birth: form.country_of_birth.trim(),
    nric_fin: form.nric_fin.trim(),
    pass_type: form.pass_type,
    address_mode: form.address_mode,
    basic_salary: form.basic_salary,
    pay_mode: form.pay_mode,
    shg_opt_out: form.shg_opt_out,
  };

  // Optional / conditional fields — only send when populated.
  const optional = {
    trade_category_id: form.trade_category_id || null,
    reporting_officer_staff_id: form.reporting_officer_staff_id,
    race: form.race,
    religion: form.religion,
    marital_status: form.marital_status,
    marriage_date: form.marriage_date || null,
    marriage_certificate_number: form.marriage_certificate_number,
    personal_email: form.personal_email,
    personal_mobile: form.personal_mobile,
    work_phone_number: form.work_phone_number,
    did_number: form.did_number,
    extension_number: form.extension_number,
    blood_type: form.blood_type,
    mom_designation: form.mom_designation,
    pr_obtained_date: form.pr_obtained_date || null,
    wp_number: form.wp_number,
    wp_application_date: form.wp_application_date || null,
    pass_issue_date: form.pass_issue_date || null,
    pass_expiry_date: form.pass_expiry_date || null,
    passport_number: form.passport_number,
    passport_expiry_date: form.passport_expiry_date || null,
    pass_renewal_reminder_days: form.pass_renewal_reminder_days === '' ? 90 : Number(form.pass_renewal_reminder_days),
    ...(() => {
      const location = inferResidentialAddressLocation({
        address: form.residential_address,
        postalCode: form.residential_postal_code,
        location: form.residential_address_location,
        country: form.residential_address_country,
      });
      return {
        residential_address:
          location === 'overseas'
            ? ensureCountryInAddress(form.residential_address, OVERSEAS_RESIDENTIAL_COUNTRY)
            : form.residential_address,
        residential_postal_code: form.residential_postal_code,
        residential_unit_number: form.residential_unit_number,
        residential_address_location: location,
        residential_address_country:
          location === 'overseas' ? OVERSEAS_RESIDENTIAL_COUNTRY : '',
      };
    })(),
    dormitory_address: form.dormitory_address,
    dormitory_postal_code: form.dormitory_postal_code,
    dormitory_unit_number: form.dormitory_unit_number,
    bank_name: form.bank_name,
    bank_account_number: form.bank_account_number,
    bank_account_holder: form.bank_account_holder,
    bank_branch_code: form.bank_branch_code,
    mom_employment_classification:
      (form.classification ? momClassFor(form.classification) : form.mom_employment_classification),
    work_email: form.work_email,
  };

  Object.entries(optional).forEach(([k, v]) => {
    if (v !== '' && v !== null && v !== undefined) payload[k] = v;
  });

  return payload;
}

export default AddEmployeeModal;
